// Copyright Epic Games, Inc. All Rights Reserved.
import React, { useEffect, useRef, useState, forwardRef, useImperativeHandle } from "react";
import { usePostHog } from 'posthog-js/react'
// @ts-ignore
import "../styles/components/PixelStreamingWrapper.css";
import {
  Config,
  AllSettings,
  PixelStreaming,
  Logger,
  LogLevel,
  AggregatedStats,
} from "@epicgames-ps/lib-pixelstreamingfrontend-ue5.6";
import MicrophonePermissionUI from "./MicrophonePermissionUI";
import { setStreamingData } from "../store/sessionSlice";
import { cleanVideoMimeType, SupportedCodecKey, supportedCodecKeys } from "../hooks/useDecoderCapabilities";
import { useDispatch } from "react-redux";
import { getCurrentOutputDeviceId } from "../utils/audioDevices";
import { reportSessionTelemetry, SessionSpec } from "../utils/sessionEndpoints";
import {
  WebRTCDiagnosticsCollector,
  createPostHogEvent,
  getWebRTCDiagnosticsConfig,
} from "../utils/webrtcDiagnostics";
import { DisplayMode } from "../utils/baseConfig";

// const printConfig = (obj: any, indent: string = ""): void => {
//   Object.entries(obj).forEach(([key, value]) => {
//     if (value === null) {
//       console.log(`${indent}${key}: null`);
//     } else if (typeof value === "object" && value !== null) {
//       console.log(`${indent}${key}:`);
//       printConfig(value, indent + "  ");
//     } else {
//       console.log(`${indent}${key}: ${value}`);
//     }
//   });
// };

// I have the feeling this might come back, so leave it here for now...
// export class PixelStreamingWithAudio extends PixelStreaming {
//   public getAudioElement(): HTMLAudioElement | null {
//     return this.webRtcController?.streamController?.audioElement || null;
//   }
// }

function getMediaStreamFrom(el: HTMLMediaElement): MediaStream | null {
  const so = el.srcObject;
  if (so && typeof (so as MediaStream).getAudioTracks === "function") {
    return so as MediaStream;
  }
  return null;
}

function elementHasLiveAudio(el: HTMLMediaElement): boolean {
  const ms = getMediaStreamFrom(el);
  if (!ms) return false;
  return ms.getAudioTracks().some(t => t.readyState === "live" && t.enabled);
}

// Cache for the live media element
let cachedLiveMediaElement: HTMLMediaElement | null = null;
function findLiveMediaElement(): HTMLMediaElement | null {
  // If we have a cached element, check if it's still valid
  if (
    cachedLiveMediaElement &&
    document.contains(cachedLiveMediaElement) &&
    elementHasLiveAudio(cachedLiveMediaElement) &&
    !cachedLiveMediaElement.paused
  ) {
    return cachedLiveMediaElement;
  }
  // Otherwise, search for a new one
  const all = Array.from(document.querySelectorAll<HTMLMediaElement>("audio,video"));
  for (const el of all) {
    if (elementHasLiveAudio(el) && !el.paused) {
      cachedLiveMediaElement = el;
      return el;
    }
  }
  cachedLiveMediaElement = null;
  return null;
}

export interface PixelStreamingWrapperProps {
  initialSettings?: Partial<AllSettings>;
  displayMode: DisplayMode;
  audioAccessRequired: boolean;
  shouldClickToPlayContinue: () => Promise<boolean>;
  onVideoStart: () => void;
  sessionId?: string;
  sessionSpec?: SessionSpec | null;
}

export interface PixelStreamingWrapperHandle {
  setAudioSpeakerId: (deviceId: string) => Promise<void>;
  setAudioSpeakerMuted: (muted: boolean) => void;
  getAudioSpeakerMuted: () => boolean;
  setAudioSpeakerVolume: (volume: number) => void;
  getAudioSpeakerVolume: () => number;
  setPreferredCodec: (codec: SupportedCodecKey) => void;
}

export const PixelStreamingWrapper = forwardRef<
  PixelStreamingWrapperHandle,
  PixelStreamingWrapperProps
>(
  (
    {
      initialSettings,
      displayMode = "fit",
      audioAccessRequired = true,
      shouldClickToPlayContinue,
      onVideoStart,
      sessionId,
      sessionSpec,
    },
    ref
  ) => {

    const posthog = usePostHog();

    // A reference to parent div element that the Pixel Streaming library attaches into:
    const videoParent = useRef<HTMLDivElement>(null);

    // Pixel streaming library instance is stored into this state variable after initialization:
    const [pixelStreaming, setPixelStreaming] = useState<PixelStreaming>();

    // A boolean state variable that determines if the Click to play overlay is shown:
    const [clickToPlayVisible, setClickToPlayVisible] = useState(false);
    // const [waitingForVideoStart, setWaitingForVideoStart] = useState(true);

    const dispatch = useDispatch();


    // the ref handle that can change the selected speaker
    useImperativeHandle(
      ref,
      () => ({
        setAudioSpeakerId: async (deviceId: string) => {
          const audioElement = findLiveMediaElement();
          if (!audioElement) { return; }
          await audioElement.setSinkId(deviceId);
        },
        setAudioSpeakerMuted: (muted: boolean) => {
          const audioElement = findLiveMediaElement();
          if (!audioElement) { return; }
          audioElement.muted = muted;
        },
        getAudioSpeakerMuted: () => {
          const audioElement = findLiveMediaElement();
          return audioElement ? audioElement.muted : false;
        },
        setAudioSpeakerVolume: (volume: number) => {
          const audioElement = findLiveMediaElement();
          if (!audioElement) {
            return;
          }
          audioElement.volume = volume; 
        },
        getAudioSpeakerVolume: () => {
          const audioElement = findLiveMediaElement();
          return audioElement ? audioElement.volume : 1.0;
        },
        setPreferredCodec: (codec: SupportedCodecKey) => {
          if (!pixelStreaming) {
            return;
          }
          if (supportedCodecKeys.includes(codec)) {
            console.log("[PixelStreamingWrapper] Setting preferred codec to:", codec);
            pixelStreaming.webRtcController?.setPreferredCodec(codec);
            window.location.reload();
          }
        },
      }),
      [pixelStreaming]
    );

    // Run on component mount:
    useEffect(() => {
      if (videoParent.current) {
        Logger.InitLogging(LogLevel.Error, true);

        console.log("[PixelStreaming] Component mount");
        console.log("[PixelStreaming] url: ", initialSettings?.ss || "Not provided");

        // Attach Pixel Streaming library to videoParent element:
        const config = new Config({ initialSettings });
        let codecNegotiationStartTime: number | null = null;

        // Log connection details
        // const wsUrl = getSignallingServerURL();
        // console.log("[PixelStreaming] Attempting connection to:", wsUrl);
        // console.log("[PixelStreaming] Configuration:");
        // printConfig(config.getSettings());

        const streaming = new PixelStreaming(config, {
          videoElementParent: videoParent.current,
        });

        // Initialize WebRTC diagnostics collector if enabled
        let diagnosticsCollector: WebRTCDiagnosticsCollector | null = null;
        const diagnosticsConfig = getWebRTCDiagnosticsConfig();
        if (sessionId && diagnosticsConfig.enabled) {
          diagnosticsCollector = new WebRTCDiagnosticsCollector(sessionId);
          console.log(
            `[PixelStreaming] WebRTC diagnostics enabled, sample rate: ${diagnosticsConfig.sampleRateMs}ms`
          );
        }

        streaming.signallingProtocol.addEventListener("onConfig", (e) =>
          console.log("[PixelStreaming] onConfig", e)
        );
        streaming.signallingProtocol.addEventListener("onOffer", (e) =>
          console.log("[PixelStreaming] onOffer", e)
        );

        // register a playStreamRejected handler to show Click to play overlay if needed:
        streaming.addEventListener("playStreamRejected", () => {
          console.log("[PixelStreaming] playStreamRejected");
          setClickToPlayVisible(true);
        });

        // Add connection event listeners for debugging
        streaming.addEventListener("webRtcAutoConnect", () => {
          console.log("[PixelStreaming] webRtcAutoConnect...");
        });

        streaming.addEventListener("webRtcConnecting", () => {
          console.log("[PixelStreaming] webRtcConnecting...");
        });

        streaming.addEventListener("webRtcConnected", () => {
          console.log("[PixelStreaming] webRtcConnected");
        });

        streaming.addEventListener("webRtcDisconnected", () => {
          console.log("[PixelStreaming] webRtcDisconnected");
        });

        streaming.addEventListener("webRtcSdp", (event) => {
          console.log("[PixelStreaming] webRtcSdp:", event);
        });

        streaming.addEventListener("videoInitialized", () => {
          console.log("[PixelStreaming] videoInitialized");
          onVideoStart();
        });

        let stats: AggregatedStats | undefined;
        let statLogCount = 0;
        let codecs: string = "";

        streaming.addEventListener("statsReceived", (event) => {
          let newStats = event.data.aggregatedStats;

          statLogCount++;
          if (!stats || statLogCount % 100 === 0) {
            stats = newStats;
            console.log("[PixelStreaming] statsReceived:", stats);
          }

          // WebRTC diagnostics sampling - collect and send to PostHog
          if (diagnosticsCollector) {
            const diagnosticsSample = diagnosticsCollector.collectSample(newStats);
            if (diagnosticsSample) {
              console.log("[PixelStreaming] sending diagnostics:", diagnosticsSample);
              posthog?.capture("webrtc_diagnostics_sample", createPostHogEvent(diagnosticsSample));
            }
          }

          const videoCodec = newStats.codecs.values().map(c => cleanVideoMimeType(c.mimeType)).find(c => c !== null);
          dispatch(setStreamingData({ videoCodec }));

          const receivedCodecs = Array.from(newStats.codecs.values()).map(c => c.mimeType);
          let newCodecs = receivedCodecs.sort().join(", ");
          if (newCodecs !== codecs || newCodecs === "") {
            codecs = newCodecs;

            const hasVideo = receivedCodecs.some(c => c.startsWith("video/"));
            const hasAudio = receivedCodecs.some(c => c.startsWith("audio/"));

            posthog?.capture("codecs_changed", { codecs, hasVideo, hasAudio, sessionId });

            // And for good measure...
            if (!hasVideo) {
              posthog?.capture("video_codec_failed", { codecs, sessionId });
            }
            console.log("[PixelStreaming] statsReceived updated codecs:", codecs, hasVideo ? "" : "no video", hasAudio ? "" : "no audio", "codec negotiation start time: ", codecNegotiationStartTime);

            // Report codec negotiation diagnostics to backend
            // Only report if we have successfully negotiated a video codec and have all required session context
            if (hasVideo && videoCodec && codecNegotiationStartTime !== null && sessionId && sessionSpec) {
              const negotiationDurationMs = Date.now() - codecNegotiationStartTime;

              // Clear the start time BEFORE making the API call to prevent duplicate reports
              // even if the API call fails
              codecNegotiationStartTime = null;

              console.log(`[PixelStreaming] Reporting codec telemetry: ${videoCodec}, duration: ${negotiationDurationMs}ms`);

              reportSessionTelemetry(
                sessionSpec,
                sessionId,
                videoCodec,
                negotiationDurationMs
              ).catch((err) => {
                console.error("[PixelStreaming] Failed to report session telemetry:", err);
              });
            }
          }
        });

        // Add detailed connection logging
        streaming.addEventListener("webRtcFailed", () => {
          const settings = config.getSettings();
          console.error("[PixelStreaming] webRtcFailed:", {
            streamingServerUrl: settings.ss,
            autoConnect: settings.AutoConnect,
            waitForStreamer: settings.WaitForStreamer,
          });

          // Try HTTPS connection to check server status
          if (settings.ss) {
            const httpsUrl = settings.ss.replace("wss:", "https:");
            fetch(httpsUrl)
              .then((response: Response) => {
                console.log("[PixelStreaming] Server status check:", {
                  url: httpsUrl,
                  status: response.status,
                  statusText: response.statusText,
                });
              })
              .catch((error: Error) => {
                console.error("[PixelStreaming] Server unreachable:", {
                  url: httpsUrl,
                  error: error.message,
                });
              });
          }
        });

        // Log all transport errors
        const originalConsoleError = console.error;
        console.error = (...args: any[]) => {
          originalConsoleError.apply(console, args);
          if (args[0] === "[Error] - Got a transport error.") {
            const settings = config.getSettings();
            console.log("[PixelStreaming] Transport error context:", {
              streamingServerUrl: settings.ss,
              error: args,
              stack: new Error().stack,
            });
          }
        };

        // Save the library instance into component state so that it can be accessed later:
        console.log("[PixelStreaming] Component mount: starting streaming.");
        setPixelStreaming(streaming);

        codecNegotiationStartTime = Date.now();

        // Clean up on component unmount:
        return () => {
          try {
            console.log("[PixelStreaming] Component unmount: disconnecting streaming.");
            streaming.disconnect();
            setPixelStreaming(undefined);
            diagnosticsCollector?.reset();
          } catch { }
        };
      }
    }, [initialSettings, onVideoStart]);

    // Monitor for audio element and apply stored speaker selection
    useEffect(() => {
      let pollInterval: number | null = null;

      const stopPolling = () => {
        if (pollInterval !== null) {
          clearInterval(pollInterval);
          pollInterval = null;
        }
      };

      const tryApplySpeakerSelection = async () => {
        const audioElement = findLiveMediaElement();
        if (audioElement) {
          const storedSpeakerId = getCurrentOutputDeviceId();
          
          if (storedSpeakerId) {
            try {
              await audioElement.setSinkId(storedSpeakerId);
              console.log("[PixelStreamingWrapper] Automatically applied stored speaker:", storedSpeakerId);
            } catch (err) {
              console.error("[PixelStreamingWrapper] Failed to apply stored speaker:", err);
            }
          }

          stopPolling();
        }
      };

      // Start polling for the audio element
      pollInterval = window.setInterval(tryApplySpeakerSelection, 250);

      // Also try immediately
      tryApplySpeakerSelection();

      // Cleanup
      return () => {
        stopPolling();
      };
    }, []);

    return (
      <div className={`pixel-streaming-container ${displayMode === "fill" ? "fill" : ""}`}>
        <div className="video-parent" ref={videoParent} />
        {clickToPlayVisible && (
          <div
            className="click-to-play-overlay"
            onClick={async () => {
              const allowed = await shouldClickToPlayContinue();
              if (allowed) {
                pixelStreaming?.play();
                setClickToPlayVisible(false);
              }
            }}
          >
            <MicrophonePermissionUI accessRequired={audioAccessRequired} />
          </div>
        )}
      </div>
    );
  }
);
